import { GROUP_DISPLAY_NAMES } from '../constants';

class ValidationService {
  static validateScheduleCollection(
    allSchedulesCollection,
    validationErrors,
    requiredFieldsGetter,
  ) {
    const realSchedules = this.filterRealSchedules(allSchedulesCollection);

    if (!realSchedules || realSchedules.length === 0) {
      return {
        isValid: false,
        message: 'Please add at least one property to complete the schedule.',
        missingFieldsByGroup: {},
        totalMissingFields: 0,
      };
    }

    const allRequiredFields = this.getAllRequiredFieldsForAllGroups(requiredFieldsGetter);
    const { missingFieldsByGroup, totalMissingFields } = this.validateRequiredFields(
      realSchedules,
      allRequiredFields,
    );

    const { duplicateAddresses } = this.validateDuplicateAddresses(allSchedulesCollection);

    const hasValidationErrors = Object.keys(validationErrors).length > 0;
    const hasEmptyFields = totalMissingFields > 0;
    const hasDuplicateAddresses = duplicateAddresses.length > 0;

    return {
      isValid: !hasEmptyFields && !hasValidationErrors && !hasDuplicateAddresses,
      message: this.getValidationMessage(hasValidationErrors, hasEmptyFields, hasDuplicateAddresses),
      missingFieldsByGroup,
      totalMissingFields,
      hasValidationErrors,
      duplicateAddresses,
    };
  }

  static filterRealSchedules(schedules) {
    return schedules.filter((schedule) => (
      schedule.id &&
      schedule.id !== '' &&
      !schedule._new &&
      !schedule._emptyRowId &&
      this.hasNonEmptyValues(schedule)
    ));
  }

  static hasNonEmptyValues(schedule) {
    return Object.keys(schedule).some((key) => (
      !['_new', '_emptyRowId', 'key'].includes(key) &&
      schedule[key] &&
      schedule[key].toString().trim() !== ''
    ));
  }

  static validateRequiredFields(schedules, allRequiredFields) {
    const missingFieldsByGroup = {};
    let totalMissingFields = 0;

    const groupedSchedules = this.groupSchedulesByType(schedules);

    Object.keys(groupedSchedules).forEach((groupType) => {
      const groupSchedules = groupedSchedules[groupType];
      const requiredFieldsForGroup = allRequiredFields[groupType] || [];

      missingFieldsByGroup[groupType] = [];

      groupSchedules.forEach((schedule, index) => {
        requiredFieldsForGroup.forEach((field) => {
          const value = schedule[field];
          if (!value || value.toString().trim() === '') {
            missingFieldsByGroup[groupType].push({
              rowIndex: index + 1,
              field,
              id: schedule.id,
            });
            totalMissingFields += 1;
          }
        });
      });
    });

    return { missingFieldsByGroup, totalMissingFields };
  }

  static groupSchedulesByType(schedules) {
    return schedules.reduce((acc, schedule) => {
      const scheduleType = schedule.group;
      if (!scheduleType) return acc;

      if (!acc[scheduleType]) {
        acc[scheduleType] = [];
      }

      acc[scheduleType].push(schedule);

      return acc;
    }, {});
  }

  static getAllRequiredFieldsForAllGroups(requiredFieldsGetter) {
    const allGroups = ['fix_and_flips', 'new_construction', 'fix_and_rent', 'investment_properties_owned', 'primary_residence'];
    const allRequiredFields = {};

    allGroups.forEach((groupName) => {
      allRequiredFields[groupName] = requiredFieldsGetter(groupName);
    });

    return allRequiredFields;
  }

  static getValidationMessage(hasValidationErrors, hasEmptyFields, hasDuplicateAddresses) {
    if (hasValidationErrors) {
      return 'Please fix validation errors before submitting.';
    }

    if (hasDuplicateAddresses) {
      return 'Please resolve duplicate addresses before submitting.';
    }

    if (hasEmptyFields) {
      return 'All required fields must be completed before proceeding.';
    }

    return null;
  }

  static getDetailedValidationMessage(validationResult, allSchedulesCollection) {
    if (!validationResult.isValid) {
      if (!allSchedulesCollection || allSchedulesCollection.length === 0) {
        return 'Please add at least one property to complete the schedule.';
      }

      if (validationResult.hasValidationErrors) {
        return 'Please fix validation errors before submitting.';
      }

      if (validationResult.duplicateAddresses && validationResult.duplicateAddresses.length > 0) {
        return 'Please resolve duplicate addresses before submitting.';
      }

      if (validationResult.totalMissingFields > 0) {
        this.createGroupMessages(validationResult.missingFieldsByGroup);

        return 'All required fields must be completed before proceeding.';
      }
    }

    return null;
  }

  static createGroupMessages(missingFieldsByGroup) {
    return Object.keys(missingFieldsByGroup)
      .filter((groupType) => missingFieldsByGroup[groupType].length > 0)
      .map((groupType) => {
        const missingFields = missingFieldsByGroup[groupType];
        const groupDisplayName = this.getGroupDisplayName(groupType);
        const fieldCount = missingFields.length;

        return `${groupDisplayName}: ${fieldCount} field${fieldCount > 1 ? 's' : ''} missing`;
      });
  }

  static getGroupDisplayName(groupType) {
    return GROUP_DISPLAY_NAMES[groupType] || groupType;
  }

  static createMissingFieldsMap(validationResult) {
    const missingFieldsMap = {};

    if (validationResult.missingFieldsByGroup) {
      Object.keys(validationResult.missingFieldsByGroup).forEach((groupType) => {
        const missingFields = validationResult.missingFieldsByGroup[groupType];
        missingFields.forEach((field) => {
          const key = `${field.id}_${field.field}`;
          missingFieldsMap[key] = true;
        });
      });
    }

    return missingFieldsMap;
  }

  static normalizeAddress(address) {
    if (!address || typeof address !== 'string') {
      return '';
    }

    return address.trim().toLowerCase().replace(/\s+/g, '');
  }

  static validateDuplicateAddresses(schedules) {
    const addressMap = new Map();

    const validSchedules = schedules
      .filter(schedule => {
        if (!schedule || !schedule.address) return false;
        const normalized = this.normalizeAddress(schedule.address);
        return normalized && normalized !== 'enter address...' && (schedule.id || schedule._emptyRowId || schedule._new);
      })
      .map(schedule => ({
        ...schedule,
        normalizedAddress: this.normalizeAddress(schedule.address),
      }));

    validSchedules.forEach((schedule) => {
      const normalizedAddress = schedule.normalizedAddress;
      if (!addressMap.has(normalizedAddress)) {
        addressMap.set(normalizedAddress, []);
      }
      addressMap.get(normalizedAddress).push(schedule);
    });

    const duplicateAddresses = [];
    addressMap.forEach((schedules, normalizedAddress) => {
      if (schedules.length > 1) {
        schedules.forEach((schedule) => {
          const scheduleKey = schedule.id || schedule._emptyRowId;
          duplicateAddresses.push({
            id: schedule.id,
            _emptyRowId: schedule._emptyRowId,
            address: schedule.address,
            normalizedAddress: normalizedAddress,
            group: schedule.group,
            conflictingWith: schedules
              .filter(other => (other.id || other._emptyRowId) !== scheduleKey)
              .map(other => ({
                id: other.id,
                _emptyRowId: other._emptyRowId,
                address: other.address,
                group: other.group,
              })),
          });
        });
      }
    });

    return { duplicateAddresses };
  }

  static createDuplicateAddressesMap(duplicateAddresses) {
    const duplicateMap = {};

    duplicateAddresses.forEach((duplicate) => {
      const key = duplicate.id ? `${duplicate.id}_address` : `${duplicate._emptyRowId}_address`;
      duplicateMap[key] = {
        isDuplicate: true,
        message: `This address has already been added`,
        conflictingWith: duplicate.conflictingWith,
      };
    });

    return duplicateMap;
  }

  static isDuplicateAddress(record, fieldName, duplicateAddresses) {
    if (fieldName !== 'address' || !record.address) {
      return false;
    }

    const normalizedAddress = this.normalizeAddress(record.address);
    if (normalizedAddress === '' || normalizedAddress === 'enter address...') {
      return false;
    }

    const recordKey = record.id || record._emptyRowId;
    if (!recordKey) return false;
    // TODO: rewrite to use Set
    return duplicateAddresses.some(duplicate => {
      const duplicateKey = duplicate.id || duplicate._emptyRowId;
      return duplicateKey === recordKey;
    });
  }
}

export default ValidationService;
